<template>
    <div class="ds-dossier-chips" role="group" aria-label="Период">
        <button
            v-for="option in periodOptions"
            :key="option.id"
            type="button"
            class="ds-dossier-chip"
            :aria-pressed="period === option.id"
            @click="period = option.id"
        >{{ option.label }}</button>
    </div>
    <span class="ds-dossier-note">По всем доступным трудоустройствам · только завершившиеся по плану смены</span>

    <!-- GET /api/user/{id}/discipline/: totals — за весь период из первой страницы, не сумма страниц. -->
    <DossierState :status="firstState.status" :height="160" error-text="Не удалось загрузить дисциплину" @retry="retryPage(1)" />
    <template v-if="totals">
        <div class="ds-dossier-pairs">
            <span>Всего нарушений</span><span class="is-strong">{{ show(totals.violationsCount) }}</span>
            <span>Опоздания</span><span>{{ withMinutes(totals.lateCount, totals.lateMinutes, 'всего ') }}</span>
            <span>Ранний уход</span><span>{{ withMinutes(totals.earlyCount, totals.earlyMinutes, '') }}</span>
            <span>Нет отметок</span><span>{{ totals.noMarksDays === null ? DASH : pluralDays(totals.noMarksDays) }}</span>
            <span>Прогулы</span><span>{{ totals.absenceDays === null ? DASH : pluralDays(totals.absenceDays) }}</span>
            <span>Отметки вручную</span><span>{{ show(totals.manualMarksCount) }}</span>
            <span>Серьёзные нарушения</span><span>{{ show(totals.seriousCount) }}</span>
        </div>

        <div class="ds-dossier-section-head"><span class="ds-dossier-section-title">События</span></div>
        <!-- «Нарушений за период нет» — только при подтверждённо пустом источнике. -->
        <div v-if="!events.length && count === 0" class="ds-dossier-state is-plain">Нарушений за период нет</div>
        <template v-else>
            <!-- События не интерактивны: без клика/ссылки на смену. -->
            <div class="ds-dossier-events">
                <template v-for="event in events" :key="event.shiftId">
                    <span class="ds-dossier-events-date">{{ formatEventDate(event.date) }}</span>
                    <span>
                        <b>{{ disciplineLabels(event).join(' · ') }}</b><template v-if="event.serious"> · серьёзное</template><br>
                        <span class="ds-dossier-muted">{{ eventDetails(event) }}</span>
                    </span>
                </template>
            </div>
            <DossierState :status="lastState.status" :height="40" error-text="Не удалось загрузить события" @retry="retryPage(pages)" />
            <button v-if="lastState.status === 'ready' && remaining > 0" type="button" class="ds-dossier-link" @click="pages++">
                Показать ещё · {{ Math.min(DISCIPLINE_PAGE_SIZE, remaining) }}
            </button>
        </template>
    </template>
</template>

<script setup lang="ts">
import { computed, ref, watch, watchEffect } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import { type DisciplinePeriod, DASH, monthName, pluralDays } from './dossierModel'
import { type DisciplineEvent, disciplineLabels, monthParam, timeRange } from './dossierActivity'
import { type DossierData, DISCIPLINE_PAGE_SIZE } from './useDossierData'
import DossierState from './DossierState.vue'

const props = defineProps<{
    data: DossierData
    // Начальный месяц досье: текущий или месяц увольнения (архив) — от него «3 месяца» и «Год».
    anchor: Dayjs
}>()
const period = defineModel<DisciplinePeriod>('period', { required: true })
// Месяц периода «месяц»: по умолчанию начальный, из «Смен» — месяц показанной недели.
const month = defineModel<Dayjs>('month', { required: true })

const periodOptions = computed(() => [
    { id: 'month' as const, label: monthName(month.value) },
    { id: '3m' as const, label: '3 месяца' },
    { id: 'year' as const, label: 'Год' },
])

// Период передаётся явно: month_from..month_to (YYYY-MM), год — с января (не больше 12 месяцев).
const bounds = computed(() => {
    if (period.value === 'month') return { from: monthParam(month.value), to: monthParam(month.value) }
    const end = props.anchor.startOf('month')
    const start = period.value === '3m' ? end.subtract(2, 'month') : end.startOf('year')
    return { from: monthParam(start), to: monthParam(end) }
})

// Загруженные страницы; смена периода сбрасывает их к первой.
const pages = ref(1)
watch(() => `${bounds.value.from}|${bounds.value.to}`, () => { pages.value = 1 })

const query = (page: number) => ({ ...bounds.value, page })
watchEffect(() => {
    for (let page = 1; page <= pages.value; page++) props.data.discipline.ensure(query(page))
})

const states = computed(() => Array.from({ length: pages.value }, (_, index) => props.data.discipline.read(query(index + 1))))
const firstState = computed(() => states.value[0])
const lastState = computed(() => states.value[states.value.length - 1])
const retryPage = (page: number) => props.data.discipline.retry(query(page))

const totals = computed(() => (firstState.value.status === 'ready' ? firstState.value.data?.totals ?? null : null))
const count = computed(() => firstState.value.data?.count ?? null)

// Строки готовых страниц подряд, без дублей по shift_id.
const events = computed(() => {
    const seen = new Set<string>()
    const list: DisciplineEvent[] = []
    for (const state of states.value) {
        if (state.status !== 'ready' || !state.data) break
        for (const event of state.data.results) {
            if (seen.has(event.shiftId)) continue
            seen.add(event.shiftId)
            list.push(event)
        }
    }
    return list
})
// Остаток по серверному count; без count «Показать ещё» не выводится.
const remaining = computed(() => {
    const lastPage = lastState.value.data
    if (count.value === null || !lastPage || lastPage.results.length < DISCIPLINE_PAGE_SIZE) return 0
    return Math.max(0, count.value - pages.value * DISCIPLINE_PAGE_SIZE)
})

const show = (value: number | null) => (value === null ? DASH : String(value))
const withMinutes = (value: number | null, minutes: number | null, prefix: string) => {
    if (value === null) return DASH
    if (!value) return '0'
    return minutes === null ? String(value) : `${value} · ${prefix}${minutes} мин`
}
const formatEventDate = (value: string) => dayjs(value).format('DD.MM')
// Подразделение различает события разных ТУ; план/факт/примечание/автор — как в ответе.
const eventDetails = (event: DisciplineEvent) => [
    event.departmentName,
    timeRange(event.planStart, event.planEnd) ? `план ${timeRange(event.planStart, event.planEnd)}` : null,
    event.kind !== 'absence' && timeRange(event.factStart, event.factEnd) ? `факт ${timeRange(event.factStart, event.factEnd)}` : null,
    event.note,
    event.manualAuthor ? `вручную: ${event.manualAuthor}` : null,
].filter(Boolean).join(' · ')
</script>
